/**
 * ComplianceAuditBar — the feature Foyr Neo cannot ship.
 *
 * Every typed AgentProposal carries a citation (NBC 2016 §4.3.6, NKBA G6,
 * ANSI A117.1 §304, RERA §3, IGBC NC v3.0, ECBC, GRIHA, Vastu) and a
 * severity (info / warn / block).  We walk every detector across every
 * room, bucket the fires by statutory framework, and render a live
 * compliance scorecard above the design stage.
 *
 * Click a framework chip to open the drawer listing every fire — each
 * row carries citation, room, fix text, and the rule that authored it.
 * The Why-chain is the product.  Neo has zero of this; visual fidelity
 * is irrelevant if you can't tell the designer whether the plan meets
 * habitable-room minima, RERA carpet-area math, or NKBA kitchen aisle.
 */
import { useMemo, useState } from "react";
import {
  CircleCheck, AlertTriangle, ShieldAlert, ShieldCheck, Gavel, Sparkles, ChevronDown,
} from "lucide-react";
import type { AgentProposal } from "../../lib/agent/types";
import type { ProjectGraph } from "../../types/ontology";

interface Props {
  readonly graph: ProjectGraph;
  readonly proposals: ReadonlyArray<AgentProposal>;
  readonly onJumpToRoom?: (roomId: string) => void;
}

interface FrameworkBucket {
  readonly key: string;
  readonly label: string;
  readonly source: string;
  readonly fires: ReadonlyArray<AgentProposal>;
  readonly info: number;
  readonly warn: number;
  readonly block: number;
}

/** Classify a citation string into a statutory / heuristic framework.  All
 *  matches are prefix-based against the citation strings that the
 *  detectors emit (see web/src/lib/agent/agentSuggestions.ts). */
function classifyCitation(citation: string): { key: string; label: string; source: string } {
  const c = citation.toLowerCase();
  if (c.startsWith("nbc-"))     return { key: "NBC",   label: "NBC 2016",      source: "Statutory · India" };
  if (c.startsWith("nkba-"))    return { key: "NKBA",  label: "NKBA Guidelines", source: "Best practice · US" };
  if (c.startsWith("ansi-") || c.startsWith("a117")) return { key: "ANSI", label: "ANSI A117.1", source: "Accessibility · US" };
  if (c.startsWith("rera-"))    return { key: "RERA",  label: "RERA 2016",      source: "Statutory · India" };
  if (c.startsWith("igbc-"))    return { key: "IGBC",  label: "IGBC NC v3.0",   source: "Sustainability · India" };
  if (c.startsWith("ecbc-"))    return { key: "ECBC",  label: "ECBC 2017",      source: "Statutory · India" };
  if (c.startsWith("griha-"))   return { key: "GRIHA", label: "GRIHA v3",       source: "Sustainability · India" };
  if (c.startsWith("vastu"))    return { key: "Vastu", label: "Vastu Shastra",  source: "Cultural · IN" };
  if (c.startsWith("affinity")) return { key: "Ontology", label: "Ontology affinity", source: "Typed" };
  if (c.startsWith("budget"))   return { key: "Budget", label: "Budget envelope", source: "Project" };
  if (c.startsWith("supply"))   return { key: "Supply", label: "Lead-time", source: "Supply chain" };
  if (c.startsWith("graph"))    return { key: "Style",  label: "Style cohesion", source: "Graph" };
  if (c.startsWith("user"))     return { key: "User",   label: "User action", source: "Audit log" };
  return { key: "Other", label: "Other", source: "—" };
}

/** Read severity + citation off an AgentProposal's typed mutation. */
function severityOf(p: AgentProposal): "info" | "warn" | "block" {
  const s = p.proposed.attrs["severity"];
  if (s === "warn" || s === "block") return s;
  return "info";
}
function citationOf(p: AgentProposal): string {
  const c = p.proposed.attrs["citation"];
  return typeof c === "string" ? c : "unknown";
}
function ruleTitleOf(p: AgentProposal): string {
  const t = p.proposed.attrs["ruleTitle"];
  return typeof t === "string" ? t : (typeof p.proposed.attrs["ruleId"] === "string" ? p.proposed.attrs["ruleId"] as string : "rule");
}
function fixTextOf(p: AgentProposal): string {
  const f = p.proposed.attrs["fix"];
  return typeof f === "string" ? f : "";
}

export function ComplianceAuditBar({ graph, proposals, onJumpToRoom }: Props): JSX.Element {
  const [openKey, setOpenKey] = useState<string | null>(null);

  // Filter out info-only "user added" fires from the compliance score —
  // those are audit-log entries, not compliance findings.
  const auditable = useMemo(() => proposals.filter((p) => {
    const c = citationOf(p);
    return !c.startsWith("user/");
  }), [proposals]);

  // Bucket every fire by statutory framework.
  const buckets = useMemo<FrameworkBucket[]>(() => {
    const m = new Map<string, FrameworkBucket>();
    for (const p of auditable) {
      const cls = classifyCitation(citationOf(p));
      const cur = m.get(cls.key) ?? {
        key: cls.key, label: cls.label, source: cls.source,
        fires: [], info: 0, warn: 0, block: 0,
      };
      const next: FrameworkBucket = {
        ...cur,
        fires: [...cur.fires, p],
        info:  cur.info  + (severityOf(p) === "info"  ? 1 : 0),
        warn:  cur.warn  + (severityOf(p) === "warn"  ? 1 : 0),
        block: cur.block + (severityOf(p) === "block" ? 1 : 0),
      };
      m.set(cls.key, next);
    }
    return [...m.values()].sort((a, b) => (b.block - a.block) || (b.warn - a.warn) || a.label.localeCompare(b.label));
  }, [auditable]);

  const totals = useMemo(() => ({
    info:  auditable.filter((p) => severityOf(p) === "info").length,
    warn:  auditable.filter((p) => severityOf(p) === "warn").length,
    block: auditable.filter((p) => severityOf(p) === "block").length,
    total: auditable.length,
  }), [auditable]);

  // Compliance score — 100% clean when no fires; degrades on warn/block
  // weights (block = 4x warn). Honest, no faking.
  const score = useMemo(() => {
    const rooms = graph.nodes.filter((n) => n.kind === "Room").length || 1;
    const maxWeighted = rooms * 8; // soft cap so single-room demos still read sensibly
    const weighted = totals.warn * 1 + totals.block * 4;
    const pct = Math.max(0, Math.min(100, Math.round(100 * (1 - weighted / maxWeighted))));
    return pct;
  }, [graph.nodes, totals]);

  const scoreTone = score >= 90 ? "ok" : score >= 70 ? "warn" : "miss";

  return (
    <section className="rounded-2xl border border-border/70 bg-cream shadow-[0_1px_2px_rgba(0,0,0,0.04)] overflow-hidden">
      <header className="flex items-center divide-x divide-border/60">
        <div className="px-3 py-1.5 flex items-center gap-2 min-w-[140px]">
          <div className={
            "grid place-items-center w-8 h-8 rounded-lg " +
            (scoreTone === "ok"   ? "bg-fern/10 text-fern" :
             scoreTone === "warn" ? "bg-foyr/10 text-foyr-deep" :
                                    "bg-foyr-deep/10 text-foyr-deep")
          }>
            {scoreTone === "ok" ? <ShieldCheck className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}
          </div>
          <div className="leading-tight">
            <div className="text-[18px] font-newyork text-ink-950 tabular leading-none">{score}%</div>
            <div className="text-[9.5px] text-ink-500 tabular">
              {totals.block > 0 ? <span className="text-foyr-deep font-medium">{totals.block} block · </span> : null}
              {totals.warn  > 0 ? <span className="text-foyr-deep">{totals.warn} warn</span> : null}
              {totals.block === 0 && totals.warn === 0 ? <span>clean</span> : null}
            </div>
          </div>
        </div>
        <div className="flex-1 px-2 py-1.5 overflow-x-auto">
          <div className="text-[9px] uppercase tracking-[0.12em] text-ink-500 mb-1 inline-flex items-center gap-1">
            <Gavel className="w-2.5 h-2.5" /> {buckets.length} frameworks · {totals.total} fires
          </div>
          {buckets.length === 0 ? (
            <div className="text-[12px] text-fern inline-flex items-center gap-1.5 py-1">
              <CircleCheck className="w-3.5 h-3.5" /> All typed rules pass. Plan is compliant.
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {buckets.map((b) => {
                const blockTone = b.block > 0 ? "block" : b.warn > 0 ? "warn" : "info";
                const isOpen = openKey === b.key;
                return (
                  <button
                    key={b.key}
                    type="button"
                    onClick={() => setOpenKey(isOpen ? null : b.key)}
                    aria-expanded={isOpen}
                    className={
                      "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border transition text-[11px] " +
                      (isOpen
                        ? "bg-ink-950 text-cream border-ink-950"
                        : blockTone === "block"
                          ? "bg-foyr-deep/10 border-foyr-deep/40 text-foyr-deep hover:bg-foyr-deep/15"
                          : blockTone === "warn"
                            ? "bg-foyr/10 border-foyr/40 text-foyr-deep hover:bg-foyr/15"
                            : "bg-fern/10 border-fern/40 text-fern hover:bg-fern/15")
                    }
                    title={`${b.label} — ${b.source}`}
                  >
                    {blockTone === "block" ? <ShieldAlert className="w-3 h-3" /> :
                     blockTone === "warn"  ? <AlertTriangle className="w-3 h-3" /> :
                                             <CircleCheck className="w-3 h-3" />}
                    <span className="font-medium">{b.label}</span>
                    <span className="tabular opacity-80">
                      {b.block > 0 ? `✗${b.block}` : ""}
                      {b.warn  > 0 ? ` ⚠${b.warn}` : ""}
                      {b.block === 0 && b.warn === 0 && b.info > 0 ? `· ${b.info}` : ""}
                    </span>
                    <ChevronDown className={"w-3 h-3 transition " + (isOpen ? "rotate-180" : "")} />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </header>

      {/* Expandable drawer — shows each fire row with citation, room, fix.
          Clicking a row jumps to that room in the stage above. */}
      {openKey !== null ? (() => {
        const b = buckets.find((x) => x.key === openKey);
        if (!b) return null;
        return (
          <div className="border-t border-border/60 bg-cream-dark/30 px-4 py-3 space-y-2">
            <div className="flex items-center gap-2 text-[11px] text-ink-500">
              <span className="font-medium text-ink-950">{b.label}</span>
              <span>·</span>
              <span>{b.source}</span>
              <span>·</span>
              <span>{b.fires.length} fire{b.fires.length === 1 ? "" : "s"}</span>
            </div>
            <ul className="space-y-1.5">
              {b.fires.map((p, i) => {
                const sev = severityOf(p);
                const cit = citationOf(p);
                const room = p.about.kind === "Room"
                  ? graph.nodes.find((n) => n.id === p.about.id && n.kind === "Room")
                  : null;
                return (
                  <li key={`${b.key}-${i}`}>
                    <button
                      type="button"
                      onClick={() => { if (room !== null && room !== undefined && onJumpToRoom) onJumpToRoom(room.id); }}
                      disabled={!room || !onJumpToRoom}
                      className={
                        "w-full text-left rounded-xl border bg-cream px-3 py-2 transition " +
                        (room && onJumpToRoom ? "border-border hover:border-foyr/40 cursor-pointer" : "border-border cursor-default")
                      }
                    >
                      <div className="flex items-start gap-2">
                        <span className={
                          "pill text-[9px] border uppercase tracking-wider mt-0.5 " +
                          (sev === "block" ? "bg-foyr-deep/10 border-foyr-deep/40 text-foyr-deep" :
                           sev === "warn"  ? "bg-foyr/10 border-foyr/40 text-foyr-deep" :
                                             "bg-cream-dark border-border text-ink-500")
                        }>{sev}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] text-ink-950 leading-snug">
                            <span className="font-medium">{ruleTitleOf(p)}</span>
                            {room !== null && room !== undefined ? <span className="text-ink-500"> · {room.label}</span> : null}
                          </div>
                          {fixTextOf(p).length > 0 ? (
                            <div className="text-[11px] text-ink-700 mt-0.5 inline-flex items-start gap-1">
                              <Sparkles className="w-3 h-3 text-foyr-deep mt-0.5 shrink-0" />
                              <span>{fixTextOf(p)}</span>
                            </div>
                          ) : null}
                          <div className="text-[10px] text-ink-500 tabular mt-1 inline-flex items-center gap-1">
                            <span className="pill bg-cream-dark border border-border text-ink-500 text-[9.5px] px-1.5 py-0">{cit}</span>
                            <span>conf {Math.round(p.confidence * 100)}%</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })() : null}
    </section>
  );
}
